import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#10b981', '#06b6d4', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899'];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border-color)',
        padding: '12px 16px',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-lg)'
      }}>
        <p style={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '4px' }}>{label}</p>
        {payload.map((item, index) => (
          <p key={index} style={{ color: item.color, fontSize: '14px' }}>
            {item.name}: {item.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const Analytics = () => {
  const { qrId } = useParams();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (qrId) {
      fetchAnalytics(qrId);
    } else {
      fetchOverview();
    }
  }, [qrId]);

  const fetchAnalytics = async (id) => {
    try {
      const response = await axios.get(`/api/analytics/qr/${id}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOverview = async () => {
    try {
      const response = await axios.get('/api/analytics/overview');
      setAnalytics({ statistics: response.data.statistics });
    } catch (error) {
      console.error('Failed to fetch overview:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading analytics...</div>;
  }

  if (!analytics) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ color: 'var(--text-muted)' }}>No analytics data available</p>
        </div>
      </div>
    );
  }

  const stats = analytics.statistics;

  return (
    <div className="container">
      <div className="dashboard-header">
        <h1 style={{ 
          background: 'var(--gradient-primary)', 
          WebkitBackgroundClip: 'text', 
          WebkitTextFillColor: 'transparent' 
        }}>
          {qrId ? 'QR Code Analytics' : 'Analytics Overview'}
        </h1>
        {qrId && (
          <Link to="/analytics" className="btn btn-secondary">
            ← Overview
          </Link>
        )}
      </div>

      {stats && (
        <>
          <div className="stats-grid">
            <div className="stat-card stagger-1">
              <h3>Total Clicks</h3>
              <div className="value">{stats.total_clicks || 0}</div>
            </div>
            <div className="stat-card stagger-2">
              <h3>Today</h3>
              <div className="value">{stats.today_clicks || 0}</div>
            </div>
            <div className="stat-card stagger-3">
              <h3>This Week</h3>
              <div className="value">{stats.week_clicks || 0}</div>
            </div>
            <div className="stat-card stagger-4">
              <h3>This Month</h3>
              <div className="value">{stats.month_clicks || 0}</div>
            </div>
          </div>
          
          {/* Platform breakdown */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <h2>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-tertiary)" strokeWidth="2" style={{ marginRight: '8px' }}>
                <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                <line x1="12" y1="18" x2="12.01" y2="18"/>
              </svg>
              Platform Breakdown
            </h2>
            <div className="stats-grid" style={{ marginBottom: 0 }}>
              <div className="stat-card" style={{ 
                background: 'linear-gradient(135deg, rgba(0, 122, 255, 0.1) 0%, rgba(88, 86, 214, 0.1) 100%)',
                borderColor: 'rgba(0, 122, 255, 0.3)'
              }}>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '20px' }}>🍎</span> iOS
                </h3>
                <div className="value" style={{ background: 'linear-gradient(135deg, #007AFF, #5856D6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  {stats.ios_clicks || 0}
                </div>
              </div>
              <div className="stat-card" style={{ 
                background: 'linear-gradient(135deg, rgba(61, 220, 132, 0.1) 0%, rgba(52, 168, 83, 0.1) 100%)',
                borderColor: 'rgba(61, 220, 132, 0.3)'
              }}>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '20px' }}>🤖</span> Android
                </h3>
                <div className="value" style={{ background: 'linear-gradient(135deg, #3DDC84, #34A853)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  {stats.android_clicks || 0}
                </div>
              </div>
              <div className="stat-card" style={{ 
                background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
                borderColor: 'rgba(6, 182, 212, 0.3)'
              }}>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '20px' }}>💻</span> Desktop
                </h3>
                <div className="value" style={{ background: 'linear-gradient(135deg, #06B6D4, #3B82F6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  {stats.desktop_clicks || 0}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
        {analytics.byDate && analytics.byDate.length > 0 && (
          <div className="card">
            <h2>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-primary)" strokeWidth="2" style={{ marginRight: '8px' }}>
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
              Clicks Over Time
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analytics.byDate}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                <XAxis 
                  dataKey="date" 
                  stroke="var(--text-muted)" 
                  tick={{ fill: 'var(--text-muted)', fontSize: 12 }}
                />
                <YAxis 
                  stroke="var(--text-muted)" 
                  tick={{ fill: 'var(--text-muted)', fontSize: 12 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="clicks" 
                  stroke="#10b981" 
                  strokeWidth={3}
                  dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, fill: '#10b981' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {analytics.byCountry && analytics.byCountry.length > 0 && (
          <div className="card">
            <h2>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-secondary)" strokeWidth="2" style={{ marginRight: '8px' }}>
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
              Top Countries
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analytics.byCountry} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                <XAxis 
                  type="number"
                  stroke="var(--text-muted)" 
                  tick={{ fill: 'var(--text-muted)', fontSize: 12 }}
                />
                <YAxis 
                  dataKey="country" 
                  type="category"
                  stroke="var(--text-muted)" 
                  tick={{ fill: 'var(--text-muted)', fontSize: 12 }}
                  width={80}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="clicks" fill="#06b6d4" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {analytics.byDevice && analytics.byDevice.length > 0 && (
          <div className="card">
            <h2>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-tertiary)" strokeWidth="2" style={{ marginRight: '8px' }}>
                <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                <line x1="12" y1="18" x2="12.01" y2="18"/>
              </svg>
              Device Distribution
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={analytics.byDevice}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="clicks"
                  nameKey="device_type"
                  label={({ device_type, percent }) => `${device_type} ${(percent * 100).toFixed(0)}%`}
                >
                  {analytics.byDevice.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {analytics.recentClicks && analytics.recentClicks.length > 0 && (
        <div className="card" style={{ marginTop: '24px' }}>
          <h2>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-warning)" strokeWidth="2" style={{ marginRight: '8px' }}>
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            Recent Clicks
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Country</th>
                  <th>City</th>
                  <th>Device</th>
                </tr>
              </thead>
              <tbody>
                {analytics.recentClicks.map((click) => (
                  <tr key={click.id}>
                    <td>
                      <span style={{ color: 'var(--text-primary)' }}>
                        {new Date(click.clicked_at).toLocaleDateString()}
                      </span>
                      <span style={{ color: 'var(--text-muted)', marginLeft: '8px', fontSize: '13px' }}>
                        {new Date(click.clicked_at).toLocaleTimeString()}
                      </span>
                    </td>
                    <td>
                      <span style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        🌍 {click.country || 'Unknown'}
                      </span>
                    </td>
                    <td>{click.city || 'Unknown'}</td>
                    <td>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: 'var(--radius-full)',
                        background: click.device_type === 'mobile' ? 'rgba(139, 92, 246, 0.15)' : 'rgba(6, 182, 212, 0.15)',
                        color: click.device_type === 'mobile' ? 'var(--accent-tertiary)' : 'var(--accent-secondary)',
                        fontSize: '12px',
                        fontWeight: 500,
                        textTransform: 'capitalize'
                      }}>
                        {click.device_type || 'Unknown'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
